// frontend/src/services/projectService.ts

import api from "./api";
import { Project, ProjectUser, ProjectStats } from "../types";

/**
 * Interface para respostas da API
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Serviço para gerenciamento de projetos
 */
// Cache para reduzir consultas ao BD
let projectsCache: {
  data: Project[] | null;
  timestamp: number;
} = {
  data: null,
  timestamp: 0
};

// Tempo de validade do cache em ms (5 minutos)
const CACHE_VALIDITY = 5 * 60 * 1000;

export const projectService = {
  /**
   * Busca todos os projetos disponíveis para o usuário autenticado
   * Usa cache para reduzir consultas ao BD
   */
  getProjects: async (forceRefresh = false): Promise<Project[]> => {
    const now = Date.now();
    const cacheExpired = now - projectsCache.timestamp > CACHE_VALIDITY;

    // Usar cache se disponível e não expirado
    if (!forceRefresh && projectsCache.data && !cacheExpired) {
      console.log('projectService: Usando cache de projetos:', projectsCache.data.length);
      return projectsCache.data;
    }

    // Buscar do servidor
    const response = await api.get<{success: boolean; data: Project[]; message?: string; count?: number}>("/api/projects");
    console.log('projectService: Projetos do usuário carregados:', response.data.data?.length || 0);

    // Atualizar cache
    projectsCache = {
      data: response.data.data || [],
      timestamp: now
    };

    return response.data.data || [];
  },

  /**
   * Busca todos os projetos (apenas para administradores)
   */
  getAllProjects: async (): Promise<Project[]> => {
    const { data } = await api.get<ApiResponse<Project[]>>("/api/projects/all");
    return data.data || [];
  },

  /**
   * Busca um projeto específico pelo ID
   * Procura primeiro no cache para reduzir consultas
   */
  getProject: async (id: string): Promise<Project> => {
    // Verificar se o projeto está em cache para evitar consulta ao BD
    if (projectsCache.data) {
      const cachedProject = projectsCache.data.find(p => p.id === id);
      if (cachedProject) {
        console.log('projectService: Projeto encontrado em cache:', id);
        return cachedProject;
      }
    }

    // Projeto não encontrado em cache, fazer requisição
    const { data } = await api.get<ApiResponse<Project>>(`/api/projects/${id}`);

    // Adicionar ao cache se ele já existir
    if (projectsCache.data) {
      const existingIndex = projectsCache.data.findIndex(p => p.id === id);
      if (existingIndex >= 0) {
        // Atualizar projeto no cache
        projectsCache.data[existingIndex] = data.data;
      } else {
        // Adicionar novo projeto ao cache
        projectsCache.data.push(data.data);
      }
    }

    return data.data;
  },

  /**
   * Busca os usuários de um projeto específico
   */
  getProjectUsers: async (projectId: string): Promise<ProjectUser[]> => {
    const { data } = await api.get<ApiResponse<ProjectUser[]>>(`/api/users/project/${projectId}`);
    return data.data || [];
  },

  /**
   * Cria um novo projeto e atualiza o cache
   */
  createProject: async (project: Omit<Project, 'id' | 'criadoEm' | 'atualizadoEm'>): Promise<Project> => {
    const { data } = await api.post<ApiResponse<Project>>('/api/projects', project);

    // Adicionar ao cache se já estiver inicializado
    if (projectsCache.data) {
      projectsCache.data.push(data.data);
    }

    return data.data;
  },

  /**
   * Atualiza um projeto existente e atualiza o cache
   */
  updateProject: async (id: string, project: Partial<Project>): Promise<Project> => {
    const { data } = await api.put<ApiResponse<Project>>(`/api/projects/${id}`, project);

    // Atualizar cache para refletir a mudança
    if (projectsCache.data) {
      const existingIndex = projectsCache.data.findIndex(p => p.id === id);
      if (existingIndex >= 0) {
        // Atualizar projeto no cache
        projectsCache.data[existingIndex] = {
          ...projectsCache.data[existingIndex],
          ...data.data
        };
      }
    }

    return data.data;
  },

  /**
   * ✅ CORRIGIDO: Atualiza apenas o campo ativo (boolean)
   * Endpoint específico com validações RBAC para owners/admins
   */
  updateProjectStatus: async (id: string, ativo: boolean): Promise<Project> => {
    const { data } = await api.patch<ApiResponse<Project>>(`/api/projects/${id}/status`, { ativo });

    // Atualizar cache para refletir a mudança
    if (projectsCache.data) {
      const existingIndex = projectsCache.data.findIndex(p => p.id === id);
      if (existingIndex >= 0) {
        projectsCache.data[existingIndex] = {
          ...projectsCache.data[existingIndex],
          ...data.data,
          ativo // ✅ CORRIGIDO: ativo depois do spread para evitar sobrescrita
        };
      }
    }

    return data.data;
  },

  /**
   * Remove um projeto e atualiza o cache
   */
  deleteProject: async (id: string): Promise<void> => {
    await api.delete(`/api/projects/${id}`);

    // Remover do cache para manter consistência
    if (projectsCache.data) {
      projectsCache.data = projectsCache.data.filter(p => p.id !== id);
    }
  },

  /**
   * Convida um usuário para o projeto
   */
  inviteUser: async (projectId: string, email: string, role: string = 'user'): Promise<{inviteId: string}> => {
    const { data } = await api.post<ApiResponse<{inviteId: string}>>(`/api/users/project/${projectId}/invite`, {
      email,
      role
    });
    return data.data;
  },

  /**
   * Remove um usuário do projeto
   */
  removeUser: async (projectId: string, userId: string): Promise<void> => {
    await api.delete(`/api/users/project/${projectId}/${userId}`);
  },

  /**
   * Busca estatísticas de um projeto com cache para reduzir consultas
   */
  // Cache para estatísticas de projetos
  projectStatsCache: new Map<string, {data: ProjectStats, timestamp: number}>(),

  getProjectStats: async (projectId: string, forceRefresh = false): Promise<ProjectStats> => {
    const now = Date.now();
    const cachedStats = projectService.projectStatsCache.get(projectId);
    const statsExpired = cachedStats ? now - cachedStats.timestamp > CACHE_VALIDITY / 2 : true; // Cache com validade menor

    // Usar cache se disponível e não expirado
    if (!forceRefresh && cachedStats && !statsExpired) {
      console.log('projectService: Usando cache de estatísticas do projeto:', projectId);
      return cachedStats.data;
    }

    // Buscar do servidor
    const { data } = await api.get<ApiResponse<ProjectStats>>(`/api/projects/${projectId}/stats`);

    // Atualizar cache
    projectService.projectStatsCache.set(projectId, {
      data: data.data,
      timestamp: now
    });

    return data.data;
  },

  /**
   * Limpa todos os caches do serviço
   * Útil ao fazer logout ou trocar de usuário
   */
  clearCache: () => {
    console.log('projectService: Limpando todos os caches');
    
    // Limpar cache de projetos
    projectsCache = {
      data: null,
      timestamp: 0
    };
    
    // Limpar cache de estatísticas
    projectService.projectStatsCache.clear();
    
    console.log('projectService: Caches limpos com sucesso');
  }
};